// Types for the custom-dashboard builder (PowerBI-style). These mirror the
// backend contracts: the `reportRegistry` Inertia prop (ReportSourceRegistry::toArray),
// the stored widget schema (dashboards.widgets[]), and the normalized query
// payloads returned by ReportQueryService.

import type { DataPointDoc } from './analytics';

export type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'kpi' | 'table';
export type ComparisonChartType = 'bar' | 'line' | 'area' | 'table';
export type Aggregation = 'count' | 'sum' | 'avg';
export type WidgetSize = 'small' | 'medium' | 'full';
export type DimensionType = 'enum' | 'config' | 'relation' | 'plain';
export type ValueFormat = 'currency' | 'number';
export type WidgetMode = 'single' | 'comparison';
export type SharedAxis = 'owner' | 'company';
export type ComparisonAxis = 'time' | SharedAxis;

// --- Registry (drives the builder pickers) ---------------------------------

export interface RegistryOption {
    value: string;
    label: string;
}

export interface RegistryDateField {
    key: string;
    label: string;
}

export interface RegistryDimension {
    key: string;
    label: string;
    type: DimensionType;
    shared: SharedAxis | null;
    options: RegistryOption[];
}

export interface RegistryMeasure {
    key: string;
    label: string;
    type: 'count' | 'numeric';
    financial: boolean;
    aggregations: Aggregation[];
}

export interface ReportSource {
    key: string;
    label: string;
    date_fields: RegistryDateField[];
    dimensions: RegistryDimension[];
    measures: RegistryMeasure[];
}

export interface ReportRegistry {
    can_view_financial: boolean;
    shared_axes: SharedAxis[];
    sources: ReportSource[];
}

// --- Stored widget schema ---------------------------------------------------

export interface WidgetFilter {
    dimension: string;
    values: string[];
}

export interface SingleWidgetConfig {
    id: string;
    title: string;
    size: WidgetSize;
    mode?: 'single';
    chart_type: ChartType;
    source: string;
    dimension: string | null;
    measure: string;
    aggregation: Aggregation;
    date_field: string | null;
    filters: WidgetFilter[];
}

export interface ComparisonSeries {
    source: string;
    measure: string;
    aggregation: Aggregation;
    date_field: string | null;
    filters: WidgetFilter[];
}

export interface ComparisonWidgetConfig {
    id: string;
    title: string;
    size: WidgetSize;
    mode: 'comparison';
    chart_type: ComparisonChartType;
    axis: ComparisonAxis;
    series: ComparisonSeries[];
}

export type WidgetConfig = SingleWidgetConfig | ComparisonWidgetConfig;

export function isComparisonWidget(widget: WidgetConfig): widget is ComparisonWidgetConfig {
    return widget.mode === 'comparison';
}

/** A superset of both widget shapes with all fields optional — the builder's in-progress state. */
export interface WidgetDraft {
    id?: string;
    title?: string;
    size?: WidgetSize;
    mode?: WidgetMode;
    chart_type?: ChartType;
    source?: string;
    dimension?: string | null;
    measure?: string;
    aggregation?: Aggregation;
    date_field?: string | null;
    filters?: WidgetFilter[];
    axis?: ComparisonAxis;
    series?: ComparisonSeries[];
}

// --- Normalized query results ----------------------------------------------

export type NormalizedChartConfig = Record<string, { label?: string; color?: string }>;

/**
 * Every non-error result carries the plain-language + SQL explanation of the
 * query that produced it, generated by App\Services\Reporting\WidgetDocumentation.
 */
interface DocumentedResult {
    doc?: DataPointDoc;
}

export interface CategoricalResult extends DocumentedResult {
    kind: 'categorical';
    chart_type: ChartType;
    data: Array<{ label: string; value: number; fill: string }>;
    chartConfig: NormalizedChartConfig;
    measure_label: string;
    x_label: string;
    format: ValueFormat;
}

export interface TemporalResult extends DocumentedResult {
    kind: 'temporal';
    chart_type: ChartType;
    data: Array<{ period: string; value: number }>;
    series: Array<{ key: string; name: string; color: string }>;
    chartConfig: NormalizedChartConfig;
    measure_label: string;
    x_label: string;
    format: ValueFormat;
}

export interface KpiResult extends DocumentedResult {
    kind: 'kpi';
    value: number;
    format: ValueFormat;
    measure_label: string;
}

export interface ComparisonResult extends DocumentedResult {
    kind: 'comparison';
    chart_type: ComparisonChartType;
    axis: ComparisonAxis;
    // rows shaped { x: string, series_0: number, series_1: number, ... }
    data: Array<Record<string, string | number>>;
    series: Array<{ key: string; name: string; color: string; format: ValueFormat }>;
    chartConfig: NormalizedChartConfig;
    x_label: string;
}

export interface ErrorResult extends DocumentedResult {
    kind: 'error';
}

export type WidgetResult = CategoricalResult | TemporalResult | KpiResult | ComparisonResult | ErrorResult;

// --- API payloads -----------------------------------------------------------

/** The store/update response shape — id and name only, straight off the model. */
export interface UserDashboardSummary {
    id: string;
    name: string;
}

/**
 * A dashboard as it appears in the analytics page's dropdown.
 *
 * `can_manage` is resolved server-side by DashboardVisibility so the client
 * never has to re-derive the Administrator wildcard; `is_owner` separates
 * "my dashboards" from "shared with me" in the picker.
 */
export interface DashboardListItem extends UserDashboardSummary {
    is_owner: boolean;
    can_manage: boolean;
    owner_name: string | null;
}

export interface DashboardDataResponse {
    dashboard: UserDashboardSummary;
    widgets: Array<WidgetConfig & { result: WidgetResult }>;
}
